import React from 'react';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { defineMessages, useIntl } from 'react-intl';
import FhirDateTime from '../../../../../../util/fhirResourceParsers/FhirDateTime';
import useFhirSearch, {
  Hits,
} from '../../../../../Surveys/util/FhirSearch/useFhirSearch';
import {
  getMedicationRequestDisplay,
  MedicationRequestAttachment,
} from '../Attachment';

const dateTimeParser = new FhirDateTime();

const messages = defineMessages({
  medication: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.medicationRequestViewModal.medication',
    defaultMessage: 'Medication',
  },
  status: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.medicationRequestViewModal.status',
    defaultMessage: 'Status',
  },
  intent: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.medicationRequestViewModal.intent',
    defaultMessage: 'Intent',
  },
  authoredOn: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.medicationRequestViewModal.authoredOn',
    defaultMessage: 'Authored On',
  },
  requester: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.medicationRequestViewModal.requester',
    defaultMessage: 'Requester',
  },
  dosage: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.medicationRequestViewModal.dosage',
    defaultMessage: 'Dosage',
  },
  reason: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.medicationRequestViewModal.reason',
    defaultMessage: 'Reason',
  },
  note: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.medicationRequestViewModal.note',
    defaultMessage: 'Note',
  },
});

const useStyles = makeStyles(theme => ({
  content: {
    padding: theme.spacing(2),
  },
  field: {
    marginBottom: theme.spacing(1.5),
  },
  value: {
    whiteSpace: 'normal',
    wordWrap: 'break-word',
  },
}));

type MedicationRequestViewModalProps = {
  attachment: MedicationRequestAttachment;
  onDismiss: () => void;
};

const MedicationRequestField = ({
  label,
  value,
}: {
  label: string;
  value?: string;
}) => {
  const classes = useStyles();
  return (
    <Box direction="column" className={classes.field}>
      <Text weight="bold">{label}</Text>
      <Text className={classes.value}>{value || '-'}</Text>
    </Box>
  );
};

const getDateDisplay = (date?: string): string | undefined =>
  date ? dateTimeParser.getDisplayValue(date) || undefined : undefined;

const getRequesterDisplay = (
  resource: fhir.MedicationRequest,
): string | undefined => {
  const requester = resource.requester;
  if (!requester || typeof requester !== 'object') return undefined;
  return (
    (requester as { agent?: { display?: string; reference?: string } }).agent
      ?.display ||
    (requester as { agent?: { display?: string; reference?: string } }).agent
      ?.reference ||
    (requester as { display?: string; reference?: string }).display ||
    (requester as { display?: string; reference?: string }).reference
  );
};

const getDosageDisplay = (
  resource: fhir.MedicationRequest,
): string | undefined => {
  const dosage = resource.dosageInstruction?.[0];
  if (!dosage) return undefined;
  const doseQuantity = dosage.doseQuantity;
  const doseQuantityDisplay = doseQuantity
    ? `${doseQuantity.value || ''} ${doseQuantity.unit || ''}`.trim()
    : undefined;
  return (
    dosage.text ||
    dosage.patientInstruction ||
    [doseQuantityDisplay, dosage.timing?.code?.text, dosage.route?.text]
      .filter(Boolean)
      .join(' ')
  );
};

const getReasonDisplay = (
  resource: fhir.MedicationRequest,
): string | undefined =>
  resource.reasonCode?.[0]?.text ||
  resource.reasonCode?.[0]?.coding?.find(coding => coding.display)?.display ||
  resource.reasonCode?.[0]?.coding?.[0]?.code;

export const MedicationRequestViewModal = ({
  attachment,
  onDismiss,
}: MedicationRequestViewModalProps) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const [response, isLoading] = useFhirSearch<Hits<fhir.MedicationRequest>>({
    type: 'select',
    columns: '*',
    from: [{ table: 'medication_request' }],
    limit: [
      { type: 'number', value: 0 },
      { type: 'number', value: 1 },
    ],
    where: {
      type: 'elasticsearch',
      query: {
        bool: {
          must: [
            {
              term: {
                'id.keyword': attachment.resource.id,
              },
            },
          ],
        },
      },
    },
  });

  const medicationRequest =
    response?.hits?.hits[0]?._source ?? attachment.resource;
  const title = getMedicationRequestDisplay(medicationRequest);

  return (
    <Modal
      isOpen={true}
      size={1}
      fullWidth
      onDismiss={onDismiss}
      title={title}
      data-testid="medication-request-view-modal"
    >
      {isLoading ? (
        <Box fullWidth justify="center" align="center" padding={4}>
          <DotLoader />
        </Box>
      ) : (
        <Box direction="column" className={classes.content}>
          <MedicationRequestField
            label={formatMessage(messages.medication)}
            value={getMedicationRequestDisplay(medicationRequest)}
          />
          <MedicationRequestField
            label={formatMessage(messages.status)}
            value={medicationRequest.status}
          />
          <MedicationRequestField
            label={formatMessage(messages.intent)}
            value={medicationRequest.intent}
          />
          <MedicationRequestField
            label={formatMessage(messages.authoredOn)}
            value={getDateDisplay(medicationRequest.authoredOn)}
          />
          <MedicationRequestField
            label={formatMessage(messages.requester)}
            value={getRequesterDisplay(medicationRequest)}
          />
          <MedicationRequestField
            label={formatMessage(messages.dosage)}
            value={getDosageDisplay(medicationRequest)}
          />
          <MedicationRequestField
            label={formatMessage(messages.reason)}
            value={getReasonDisplay(medicationRequest)}
          />
          <MedicationRequestField
            label={formatMessage(messages.note)}
            value={medicationRequest.note?.[0]?.text}
          />
        </Box>
      )}
    </Modal>
  );
};
